import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Edit3, Trash2, Eye, EyeOff, ExternalLink, Save, X, Download, Check as CheckIcon, AlertTriangle, Info, Clock, Zap } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_OPTIONS = [
  "Menopause & Identity",
  "Clinical Hypnotherapy",
  "Nervous System",
  "Personal Growth & Reinvention",
  "Life Transitions",
  "Emotional Wellbeing",
  "Mindset",
  "Confidence",
];

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

function emptyDraft() {
  return {
    slug: "",
    title: "",
    subtitle: "",
    excerpt: "",
    body: "",
    featured_image: "",
    category: CATEGORY_OPTIONS[0],
    tags: "",
    meta_description: "",
    published: true,
    scheduled_publish_at: "",  // ISO-8601 UTC, empty = no schedule
  };
}

// ─── Melbourne local time helpers ────────────────────────────────────────────
// The scheduler runs on Australia/Melbourne wall-clock (handles AEST↔AEDT
// DST transitions automatically). The DB stores UTC. These two helpers keep
// the UI in Pip's local time while persisting UTC.
const MELBOURNE_TZ = "Australia/Melbourne";

function utcIsoToMelbourneInputValue(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    // Get parts in Melbourne timezone
    const parts = new Intl.DateTimeFormat("en-AU", {
      timeZone: MELBOURNE_TZ,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  } catch { return ""; }
}

function melbourneInputValueToUtcIso(local) {
  // local looks like "2026-09-15T08:00" — interpret as Australia/Melbourne
  // wall-clock and convert to UTC. We compute the offset from a probe Date.
  if (!local) return "";
  try {
    // Parse the local input as if it were UTC first, then correct for the
    // Melbourne offset AT that wall-clock moment (so DST is handled).
    const [d, t] = local.split("T");
    const [y, m, day] = d.split("-").map(Number);
    const [hh, mm] = t.split(":").map(Number);
    const asUtcGuess = Date.UTC(y, m - 1, day, hh, mm, 0);
    // Ask Intl what that timestamp looks like in Melbourne — if not the
    // same wall-clock, we adjust by the difference (this is the standard
    // one-round-trip trick for "interpret local time in named tz").
    const fmt = new Intl.DateTimeFormat("en-AU", {
      timeZone: MELBOURNE_TZ,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
    const parts = fmt.formatToParts(new Date(asUtcGuess)).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const asMelb = Date.UTC(
      parseInt(parts.year), parseInt(parts.month) - 1, parseInt(parts.day),
      parseInt(parts.hour), parseInt(parts.minute), parseInt(parts.second)
    );
    const offsetMs = asMelb - asUtcGuess;  // Melbourne is ahead of UTC → positive offset
    return new Date(asUtcGuess - offsetMs).toISOString();
  } catch { return ""; }
}

function formatScheduledMelbourne(iso) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: MELBOURNE_TZ,
      weekday: "short", day: "numeric", month: "short",
      hour: "numeric", minute: "2-digit", hour12: true,
      timeZoneName: "short",
    }).format(new Date(iso));
  } catch { return iso; }
}

// ─── ApiTokensPanel ─────────────────────────────────────────────────────────
// Admin-only management for machine-to-machine API tokens (editor-scoped).
// Purpose: give AI/headless helpers a bearer-token path that bypasses the
// browser cookie login (Cloudflare bot-blocking) while staying editor-limited.
function ApiTokensPanel() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDays, setNewDays] = useState(60);
  const [freshToken, setFreshToken] = useState(null); // {token, name, expires_at}
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/api-tokens");
      setTokens(r.data || []);
    } catch {
      toast.error("Couldn't load API tokens.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await api.post("/admin/api-tokens", {
        name: newName.trim(),
        expires_in_days: parseInt(newDays, 10) || 60,
      });
      setFreshToken(r.data);
      setNewName("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't create token.");
    } finally { setCreating(false); }
  };

  const revoke = async (t) => {
    if (!window.confirm(`Revoke "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.post(`/admin/api-tokens/${t.id}/revoke`);
      toast.success("Token revoked.");
      await load();
    } catch {
      toast.error("Revoke failed.");
    }
  };

  const copyFresh = () => {
    if (!freshToken) return;
    navigator.clipboard.writeText(freshToken.token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <section className="mt-16 pt-12 border-t border-hairline" data-testid="api-tokens-panel">
      <div className="flex items-center gap-3 mb-2">
        <h2 className="font-serif-display text-2xl text-ink">API tokens</h2>
        <span className="text-[0.65rem] tracking-wide uppercase bg-ink-soft/10 text-ink-soft px-2 py-0.5 rounded-full">
          Editor-scoped · machine-to-machine
        </span>
      </div>
      <p className="text-sm text-ink-soft mb-6 max-w-3xl leading-relaxed">
        Bearer tokens for AI helpers or scripts that can&rsquo;t use a browser login (e.g. headless
        assistants blocked by Cloudflare bot protection). Each token can create, edit and schedule
        Journal drafts &mdash; but cannot publish, delete, seed, or access any other admin data.
        Tokens are shown once at creation. Revoke immediately when no longer needed.
      </p>

      {/* Fresh token banner — shown once after creation */}
      {freshToken && (
        <div
          className="bg-sage/5 border-2 border-sage/40 rounded-2xl p-5 mb-6"
          data-testid="api-tokens-fresh-banner"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="eyebrow text-sage-dark mb-1">Copy this now — it won&rsquo;t be shown again</div>
              <div className="font-medium text-ink">{freshToken.name}</div>
              <div className="text-xs text-ink-soft mt-1">
                Expires {formatScheduledMelbourne(freshToken.expires_at)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setFreshToken(null)}
              className="text-ink-soft hover:text-ink"
              aria-label="Dismiss"
            ><X size={14} strokeWidth={2}/></button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <code
              className="flex-1 font-mono text-xs bg-white border border-hairline rounded-lg px-3 py-2.5 text-ink break-all select-all"
              data-testid="api-tokens-fresh-value"
            >{freshToken.token}</code>
            <button
              type="button"
              onClick={copyFresh}
              className="inline-flex items-center gap-1.5 bg-sage hover:bg-sage-dark text-cream rounded-lg px-4 py-2 text-sm font-medium transition"
              data-testid="api-tokens-copy-btn"
            >
              {copied ? <><CheckIcon size={12} strokeWidth={2}/> Copied</> : "Copy"}
            </button>
          </div>
          <p className="mt-3 text-xs text-ink-soft leading-relaxed">
            Send this to your helper securely (1Password, encrypted note, etc). They authenticate
            by sending <code className="text-ink">Authorization: Bearer &lt;token&gt;</code> on every
            request to <code className="text-ink">/api/admin/insights</code>.
          </p>
        </div>
      )}

      {/* Create new token */}
      <form onSubmit={create} className="flex flex-col sm:flex-row gap-2 mb-6" data-testid="api-tokens-create-form">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Token name — e.g. Wingman Sept–Oct 2026"
          className="flex-1 bg-cream-soft border border-hairline rounded-xl px-4 py-2.5 text-ink focus:outline-none focus:border-sage text-sm"
          data-testid="api-tokens-name-input"
          required
          maxLength={80}
        />
        <input
          type="number"
          value={newDays}
          onChange={(e) => setNewDays(e.target.value)}
          min={1} max={180}
          className="w-full sm:w-32 bg-cream-soft border border-hairline rounded-xl px-4 py-2.5 text-ink focus:outline-none focus:border-sage text-sm"
          data-testid="api-tokens-days-input"
          title="Days until this token expires (max 180)"
        />
        <button
          type="submit"
          disabled={creating || !newName.trim()}
          className="inline-flex items-center justify-center gap-2 bg-ink hover:bg-ink/90 disabled:opacity-40 text-cream rounded-xl px-5 py-2.5 text-sm font-medium transition"
          data-testid="api-tokens-create-btn"
        >
          <Plus size={14} strokeWidth={2}/> Create token
        </button>
      </form>

      {/* Token list */}
      {loading ? (
        <div className="text-ink-soft text-sm py-4">Loading tokens…</div>
      ) : tokens.length === 0 ? (
        <div className="bg-white border border-hairline rounded-xl p-6 text-center text-ink-soft text-sm">
          No API tokens yet. Create one above.
        </div>
      ) : (
        <div className="bg-white border border-hairline rounded-2xl overflow-hidden">
          {tokens.map((t, i) => (
            <div
              key={t.id}
              className={`flex flex-col md:flex-row md:items-center gap-3 p-4 md:p-5 ${i !== 0 ? "border-t border-hairline" : ""}`}
              data-testid={`api-token-row-${t.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-medium text-ink text-sm">{t.name}</span>
                  <span className={`text-[0.6rem] tracking-wide uppercase px-2 py-0.5 rounded-full ${
                    t.status === "active" ? "bg-sage/15 text-sage-dark"
                    : t.status === "revoked" ? "bg-ink-soft/15 text-ink-soft"
                    : "bg-gold/20 text-gold-dark"
                  }`}>{t.status}</span>
                </div>
                <div className="text-xs text-ink-soft space-x-2">
                  <span>Created {formatScheduledMelbourne(t.created_at)}</span>
                  <span>·</span>
                  <span>Expires {formatScheduledMelbourne(t.expires_at)}</span>
                  {t.last_used_at && <><span>·</span><span>Last used {formatScheduledMelbourne(t.last_used_at)}</span></>}
                </div>
              </div>
              {t.status === "active" && (
                <button
                  type="button"
                  onClick={() => revoke(t)}
                  className="inline-flex items-center gap-1.5 border border-hairline text-ink-soft hover:border-red-400 hover:text-red-500 rounded-full px-4 py-2 text-xs font-medium transition"
                  data-testid={`api-token-revoke-${t.id}`}
                >
                  <Trash2 size={12} strokeWidth={1.8}/> Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function AdminInsights() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, "new" or article object = editor
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [previewing, setPreviewing] = useState(false); // preview-as-live modal

  // Live SEO checklist — runs entirely on the draft, no API calls.
  const seoChecks = useMemo(() => {
    const title = (draft.title || "").trim();
    const slug = (draft.slug || "").trim();
    const excerpt = (draft.excerpt || "").trim();
    const meta = (draft.meta_description || "").trim();
    const body = draft.body || "";
    const subtitle = (draft.subtitle || "").trim();
    const wordCount = body.split(/\s+/).filter(Boolean).length;
    const headingCount = (body.match(/^##\s/gm) || []).length;
    const internalLinks = (body.match(/\]\((\/insights\/[^)]+|\/[a-z][^)]*)\)/g) || []).length;
    const externalLinks = (body.match(/\]\(https?:\/\/[^)]+\)/g) || []).length;
    return [
      {
        label: "Title 40–60 characters",
        ok: title.length >= 40 && title.length <= 60,
        detail: `${title.length} chars`,
        soft: title.length >= 30 && title.length < 40,
      },
      {
        label: "URL slug set & clean",
        ok: slug.length > 3 && /^[a-z0-9-]+$/.test(slug),
        detail: slug ? `/${slug}` : "missing",
      },
      {
        label: "Meta description 120–160 chars",
        ok: meta.length >= 120 && meta.length <= 160,
        detail: `${meta.length} chars`,
        soft: meta.length > 0 && meta.length < 120,
      },
      {
        label: "Excerpt present (1–3 sentences)",
        ok: excerpt.length >= 60 && excerpt.length <= 320,
        detail: `${excerpt.length} chars`,
        soft: excerpt.length > 0 && excerpt.length < 60,
      },
      {
        label: "Subtitle adds context",
        ok: subtitle.length >= 20,
        detail: subtitle ? `${subtitle.length} chars` : "optional",
        soft: true,
      },
      {
        label: "Body 600+ words",
        ok: wordCount >= 600,
        detail: `${wordCount} words`,
        soft: wordCount >= 300 && wordCount < 600,
      },
      {
        label: "At least 2 section headings (##)",
        ok: headingCount >= 2,
        detail: `${headingCount} found`,
      },
      {
        label: "At least 1 internal link",
        ok: internalLinks >= 1,
        detail: `${internalLinks} internal · ${externalLinks} external`,
        soft: true,
      },
      {
        label: "Featured image set",
        ok: !!draft.featured_image,
        detail: draft.featured_image ? "set" : "missing",
        soft: true,
      },
    ];
  }, [draft]);

  const seoScore = useMemo(() => {
    const total = seoChecks.length;
    const passed = seoChecks.filter((c) => c.ok).length;
    return { passed, total, pct: Math.round((passed / total) * 100) };
  }, [seoChecks]);

  const handleSeedDefaults = async () => {
    if (
      !window.confirm(
        "Push the 7 canonical articles? Existing slugs will be UPDATED in place (titles, copy, meta — preserving SEO equity), and any missing articles will be CREATED. Safe to run any time."
      )
    ) {
      return;
    }
    setSeeding(true);
    try {
      const r = await api.post("/admin/insights/seed");
      const { created = [], updated = [], errors = [] } = r.data || {};
      if (errors.length === 0) {
        toast.success(
          `Done. ${updated.length} updated, ${created.length} created.`,
          { duration: 6000 }
        );
      } else {
        toast.error(
          `Finished with ${errors.length} error${errors.length === 1 ? "" : "s"}. Check the network tab.`,
          { duration: 8000 }
        );
      }
      await loadList();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Push failed — please try again."
      );
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    // user === null  → still checking (AuthContext hasn't resolved /auth/me yet)
    // user === false → not logged in
    // user object    → logged in
    if (user === null) return;
    if (!user || (user.role !== "admin" && user.role !== "editor")) {
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/insights");
      setList(r.data || []);
    } catch (err) {
      toast.error("Couldn't load insights.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "editor") loadList();
  }, [user, loadList]);

  const openNew = () => {
    setDraft(emptyDraft());
    setEditing("new");
  };

  const openEdit = async (item) => {
    try {
      const r = await api.get(`/admin/insights/${item.id}`);
      const a = r.data;
      setDraft({
        slug: a.slug || "",
        title: a.title || "",
        subtitle: a.subtitle || "",
        excerpt: a.excerpt || "",
        body: a.body || "",
        featured_image: a.featured_image || "",
        category: a.category || CATEGORY_OPTIONS[0],
        tags: (a.tags || []).join(", "),
        meta_description: a.meta_description || "",
        published: a.published !== false,
        scheduled_publish_at: a.scheduled_publish_at || "",
      });
      setEditing(a);
    } catch {
      toast.error("Couldn't load article.");
    }
  };

  const save = async () => {
    if (!draft.title || !draft.excerpt || !draft.body || !draft.slug) {
      toast.error("Title, slug, excerpt and body are all required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: draft.slug,
        title: draft.title,
        subtitle: draft.subtitle || null,
        excerpt: draft.excerpt,
        body: draft.body,
        featured_image: draft.featured_image || null,
        category: draft.category,
        tags: draft.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        meta_description: draft.meta_description || null,
        published: draft.published,
        // Empty string = "clear schedule" (server treats "" as None).
        // Any non-empty value is a UTC ISO string produced by
        // melbourneInputValueToUtcIso() at input change time.
        scheduled_publish_at: draft.scheduled_publish_at || "",
      };
      if (editing === "new") {
        await api.post("/admin/insights", payload);
        toast.success("Article created.");
      } else {
        await api.patch(`/admin/insights/${editing.id}`, payload);
        toast.success("Article updated.");
      }
      setEditing(null);
      loadList();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (item) => {
    try {
      await api.patch(`/admin/insights/${item.id}`, { published: !item.published });
      toast.success(item.published ? "Unpublished." : "Published.");
      loadList();
    } catch {
      toast.error("Couldn't update.");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/insights/${item.id}`);
      toast.success("Deleted.");
      loadList();
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  if (user === null || !user || (user.role !== "admin" && user.role !== "editor")) {
    return <div className="min-h-screen flex items-center justify-center text-ink-soft">Verifying…</div>;
  }

  const isEditor = user.role === "editor";
  const isAdmin = user.role === "admin";

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-insights-page">
      <header className="border-b border-hairline bg-cream/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-ink-soft hover:text-sage transition flex items-center gap-1 text-sm" data-testid="admin-insights-back">
              <ArrowLeft size={16} strokeWidth={1.8} /> Admin
            </Link>
            <span className="text-ink-soft/40">·</span>
            <span className="font-serif-display text-xl text-ink">The Journal CMS</span>
            {isEditor && (
              <span
                className="ml-2 inline-flex items-center gap-1.5 bg-gold/15 text-gold-dark px-2.5 py-1 rounded-full text-[0.65rem] tracking-[0.15em] uppercase font-semibold"
                data-testid="admin-insights-editor-badge"
                title="Editor mode — you can create and edit drafts. Publishing and deletion require Pip."
              >
                <Info size={11} strokeWidth={2.4} /> Editor mode
              </span>
            )}
          </div>
          {editing === null ? (
            <div className="flex items-center gap-3">
              {isAdmin && (
                <button
                  type="button"
                  onClick={handleSeedDefaults}
                  disabled={seeding}
                  title="Push the 7 canonical articles (idempotent — updates existing slugs, creates missing)"
                  className="inline-flex items-center gap-2 bg-cream-soft hover:bg-cream border border-hairline text-ink rounded-full px-4 py-2.5 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="admin-insights-seed-btn"
                >
                  <Download size={14} strokeWidth={2} /> {seeding ? "Pushing…" : "Push canonical 7"}
                </button>
              )}
              <button
                type="button"
                onClick={openNew}
                className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-5 py-2.5 text-sm font-medium transition"
                data-testid="admin-insights-new-btn"
              >
                <Plus size={14} strokeWidth={2} /> New article
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-sm font-medium transition"
              data-testid="admin-insights-close-editor"
            >
              <X size={14} strokeWidth={2} /> Close editor
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {editing === null ? (
          // LIST
          <>
            <h1 className="font-serif-display text-3xl text-ink mb-2">Articles</h1>
            <p className="text-sm text-ink-soft mb-8">{list.length} total · click any to edit</p>
            {loading ? (
              <div className="text-ink-soft py-8">Loading…</div>
            ) : list.length === 0 ? (
              <div className="bg-white border border-hairline rounded-2xl p-10 text-center text-ink-soft">
                No articles yet. Click &ldquo;New article&rdquo; to write your first.
              </div>
            ) : (
              <div className="bg-white border border-hairline rounded-2xl overflow-hidden">
                {list.map((item, i) => (
                  <div
                    key={item.id}
                    className={`flex flex-col md:flex-row md:items-center gap-4 p-5 md:p-6 ${
                      i !== 0 ? "border-t border-hairline" : ""
                    }`}
                    data-testid={`admin-insight-row-${item.slug}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-[0.7rem] tracking-wide uppercase text-sage font-medium">{item.category}</span>
                        {!item.published && !item.scheduled_publish_at && (
                          <span className="text-[0.65rem] tracking-wide uppercase bg-ink-soft/10 text-ink-soft px-2 py-0.5 rounded-full">Draft</span>
                        )}
                        {!item.published && item.scheduled_publish_at && (
                          <span
                            className="text-[0.65rem] tracking-wide uppercase bg-gold/15 text-gold-dark px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                            data-testid={`admin-insight-scheduled-badge-${item.slug}`}
                            title={`Scheduled for ${formatScheduledMelbourne(item.scheduled_publish_at)}`}
                          >
                            <Clock size={10} strokeWidth={2} />
                            Scheduled · {formatScheduledMelbourne(item.scheduled_publish_at)}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="text-left font-serif-display text-xl text-ink hover:text-sage transition"
                      >
                        {item.title}
                      </button>
                      <div className="mt-1.5 text-xs text-ink-soft">
                        /{item.slug} · {item.reading_time_minutes} min
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {item.published && (
                        <a
                          href={`/insights/${item.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-hairline text-ink-soft hover:border-sage hover:text-sage transition"
                          aria-label="View on site"
                          data-testid={`admin-insight-view-${item.slug}`}
                        >
                          <ExternalLink size={14} strokeWidth={1.8} />
                        </a>
                      )}
                      {!item.published && !isEditor && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await api.post(`/admin/insights/${item.id}/publish-now`);
                              toast.success("Published live.");
                              await loadList();
                            } catch (err) {
                              toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Publish failed.");
                            }
                          }}
                          className="h-9 inline-flex items-center gap-1.5 px-3 rounded-full bg-sage hover:bg-sage-dark text-cream text-xs font-medium transition"
                          data-testid={`admin-insight-publish-now-${item.slug}`}
                          title={item.scheduled_publish_at ? "Override the schedule and publish immediately" : "Publish this draft immediately"}
                        >
                          <Zap size={12} strokeWidth={2} />
                          Publish now
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => togglePublish(item)}
                        className={`w-9 h-9 inline-flex items-center justify-center rounded-full border border-hairline text-ink-soft hover:border-sage hover:text-sage transition ${isEditor ? "hidden" : ""}`}
                        aria-label={item.published ? "Unpublish" : "Publish"}
                        data-testid={`admin-insight-toggle-${item.slug}`}
                      >
                        {item.published ? <Eye size={14} strokeWidth={1.8} /> : <EyeOff size={14} strokeWidth={1.8} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-hairline text-ink-soft hover:border-sage hover:text-sage transition"
                        aria-label="Edit"
                        data-testid={`admin-insight-edit-${item.slug}`}
                      >
                        <Edit3 size={14} strokeWidth={1.8} />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(item)}
                        className={`w-9 h-9 inline-flex items-center justify-center rounded-full border border-hairline text-ink-soft hover:border-red-400 hover:text-red-500 transition ${isEditor ? "hidden" : ""}`}
                        aria-label="Delete"
                        data-testid={`admin-insight-delete-${item.slug}`}
                      >
                        <Trash2 size={14} strokeWidth={1.8} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!isEditor && <ApiTokensPanel />}
          </>
        ) : (
          // EDITOR
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-5">
              <h1 className="font-serif-display text-3xl text-ink">
                {editing === "new" ? "New article" : "Edit article"}
              </h1>

              <div>
                <label className="eyebrow block mb-2 text-ink-soft">Title</label>
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDraft((d) => ({
                      ...d,
                      title: v,
                      slug: (editing === "new" && !d.slug) || d.slug === slugify(d.title) ? slugify(v) : d.slug,
                    }));
                  }}
                  className="w-full bg-white border border-hairline rounded-xl px-4 py-3 text-ink focus:outline-none focus:border-sage"
                  data-testid="admin-insight-title-input"
                />
              </div>

              <div>
                <label className="eyebrow block mb-2 text-ink-soft">URL slug</label>
                <div className="flex items-center bg-white border border-hairline rounded-xl overflow-hidden">
                  <span className="px-4 text-ink-soft text-sm">/insights/</span>
                  <input
                    type="text"
                    value={draft.slug}
                    onChange={(e) => setDraft((d) => ({ ...d, slug: slugify(e.target.value) }))}
                    className="flex-1 bg-transparent py-3 pr-4 text-ink focus:outline-none"
                    data-testid="admin-insight-slug-input"
                  />
                </div>
              </div>

              <div>
                <label className="eyebrow block mb-2 text-ink-soft">Subtitle (optional)</label>
                <input
                  type="text"
                  value={draft.subtitle}
                  onChange={(e) => setDraft((d) => ({ ...d, subtitle: e.target.value }))}
                  className="w-full bg-white border border-hairline rounded-xl px-4 py-3 text-ink focus:outline-none focus:border-sage"
                  data-testid="admin-insight-subtitle-input"
                />
              </div>

              <div>
                <label className="eyebrow block mb-2 text-ink-soft">Excerpt (1-3 sentences, shown on cards)</label>
                <textarea
                  rows={3}
                  value={draft.excerpt}
                  onChange={(e) => setDraft((d) => ({ ...d, excerpt: e.target.value }))}
                  className="w-full bg-white border border-hairline rounded-xl px-4 py-3 text-ink focus:outline-none focus:border-sage resize-none"
                  data-testid="admin-insight-excerpt-input"
                />
              </div>

              <div>
                <label className="eyebrow block mb-2 text-ink-soft">
                  Body (markdown supported · use ## for section headings, ### for subheadings, - for bullets, **bold**, *italic*)
                </label>
                <textarea
                  rows={24}
                  value={draft.body}
                  onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                  className="w-full bg-white border border-hairline rounded-xl px-4 py-3 text-ink font-mono text-sm leading-relaxed focus:outline-none focus:border-sage resize-y"
                  data-testid="admin-insight-body-input"
                />
                <p className="mt-2 text-xs text-ink-soft">
                  {draft.body.split(/\s+/).filter(Boolean).length} words · approx{" "}
                  {Math.max(1, Math.round(draft.body.split(/\s+/).filter(Boolean).length / 225))} min read
                </p>
              </div>
            </div>

            <aside className="space-y-5">
              <div className="bg-white border border-hairline rounded-2xl p-5 space-y-5 sticky top-24">
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Status</label>
                  {isEditor ? (
                    <div
                      className="flex items-start gap-2.5 bg-gold/10 border border-gold/30 rounded-xl px-3 py-2.5 text-xs text-ink leading-relaxed"
                      data-testid="admin-insight-editor-status-notice"
                    >
                      <Info size={13} strokeWidth={2.2} className="text-gold-dark flex-shrink-0 mt-0.5" />
                      <span>
                        Saved as <strong>draft</strong>. Pip will review and publish.
                      </span>
                    </div>
                  ) : (
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={draft.published}
                        onChange={(e) => setDraft((d) => ({ ...d, published: e.target.checked }))}
                        className="w-4 h-4"
                        data-testid="admin-insight-published-toggle"
                      />
                      <span className="text-sm text-ink">{draft.published ? "Published" : "Draft"}</span>
                    </label>
                  )}
                </div>

                {/* SCHEDULE PUBLISH — visible when this article is a draft OR
                    when the user is an editor (editors always create drafts,
                    even if the local `published` flag hasn't yet flipped).
                    Editors CAN set this (unlike the publish toggle above),
                    so Wingman can queue posts to auto-release. Stored as UTC
                    but rendered/edited in Australia/Melbourne local time. */}
                {(!draft.published || isEditor) && (
                  <div data-testid="admin-insight-schedule-block">
                    <label className="eyebrow block mb-2 text-ink-soft">
                      Schedule publish (Melbourne local time)
                    </label>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <input
                        type="datetime-local"
                        value={utcIsoToMelbourneInputValue(draft.scheduled_publish_at)}
                        onChange={(e) => {
                          const local = e.target.value;
                          setDraft((d) => ({
                            ...d,
                            scheduled_publish_at: local ? melbourneInputValueToUtcIso(local) : "",
                          }));
                        }}
                        className="flex-1 bg-cream-soft border border-hairline rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-sage text-sm"
                        data-testid="admin-insight-schedule-input"
                      />
                      {draft.scheduled_publish_at && (
                        <button
                          type="button"
                          onClick={() => setDraft((d) => ({ ...d, scheduled_publish_at: "" }))}
                          className="text-xs text-ink-soft hover:text-ink underline underline-offset-2 sm:px-2"
                          data-testid="admin-insight-schedule-clear"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {draft.scheduled_publish_at ? (
                      <p className="mt-2 text-xs text-sage-dark leading-relaxed">
                        Will auto-publish{" "}
                        <strong>{formatScheduledMelbourne(draft.scheduled_publish_at)}</strong>.
                        Handles daylight-saving automatically.
                      </p>
                    ) : (
                      <p className="mt-2 text-xs text-ink-soft/70 leading-relaxed">
                        Leave blank to keep as a draft. Set a future date/time and the article will
                        publish automatically at that moment (in Melbourne time).
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Category</label>
                  <select
                    value={draft.category}
                    onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
                    className="w-full bg-cream-soft border border-hairline rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-sage text-sm"
                    data-testid="admin-insight-category-select"
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Tags (comma-separated)</label>
                  <input
                    type="text"
                    placeholder="menopause, hypnotherapy, identity"
                    value={draft.tags}
                    onChange={(e) => setDraft((d) => ({ ...d, tags: e.target.value }))}
                    className="w-full bg-cream-soft border border-hairline rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-sage text-sm"
                    data-testid="admin-insight-tags-input"
                  />
                </div>

                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Featured image URL (optional)</label>
                  <input
                    type="url"
                    placeholder="https://…"
                    value={draft.featured_image}
                    onChange={(e) => setDraft((d) => ({ ...d, featured_image: e.target.value }))}
                    className="w-full bg-cream-soft border border-hairline rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-sage text-sm"
                    data-testid="admin-insight-image-input"
                  />
                </div>

                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Meta description (SEO)</label>
                  <textarea
                    rows={3}
                    value={draft.meta_description}
                    onChange={(e) => setDraft((d) => ({ ...d, meta_description: e.target.value }))}
                    className="w-full bg-cream-soft border border-hairline rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-sage text-sm resize-none"
                    data-testid="admin-insight-meta-input"
                  />
                  <p className="mt-1 text-[0.7rem] text-ink-soft/70">
                    {draft.meta_description.length} / 200 characters
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setPreviewing(true)}
                  disabled={!draft.title || !draft.body}
                  className="w-full inline-flex items-center justify-center gap-2 bg-cream-soft hover:bg-cream border border-hairline text-ink rounded-full px-5 py-2.5 text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="admin-insight-preview-btn"
                  title="Preview the draft exactly as it will appear on the live site"
                >
                  <Eye size={14} strokeWidth={2} /> Preview as live article
                </button>

                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="w-full inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-5 py-3 font-medium transition disabled:opacity-50"
                  data-testid="admin-insight-save-btn"
                >
                  {saving ? "Saving…" : (
                    <>
                      <Save size={14} strokeWidth={2} /> Save article
                    </>
                  )}
                </button>
              </div>

              <div
                className="bg-white border border-hairline rounded-2xl p-5 space-y-4"
                data-testid="admin-insight-seo-checklist"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-serif-display text-lg text-ink">SEO checklist</h3>
                  <span
                    className={`text-xs tracking-wide uppercase font-semibold px-2.5 py-1 rounded-full ${
                      seoScore.pct >= 80
                        ? "bg-sage/15 text-sage-dark"
                        : seoScore.pct >= 50
                        ? "bg-gold/15 text-gold-dark"
                        : "bg-ink-soft/10 text-ink-soft"
                    }`}
                    data-testid="admin-insight-seo-score"
                  >
                    {seoScore.passed}/{seoScore.total}
                  </span>
                </div>
                <ul className="space-y-2.5">
                  {seoChecks.map((c, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2.5 text-xs leading-relaxed"
                      data-testid={`admin-insight-seo-check-${i}`}
                    >
                      <span
                        className={`flex-shrink-0 w-4 h-4 rounded-full inline-flex items-center justify-center mt-0.5 ${
                          c.ok
                            ? "bg-sage/20 text-sage-dark"
                            : c.soft
                            ? "bg-gold/20 text-gold-dark"
                            : "bg-ink-soft/15 text-ink-soft"
                        }`}
                      >
                        {c.ok ? (
                          <CheckIcon size={10} strokeWidth={3} />
                        ) : c.soft ? (
                          <AlertTriangle size={9} strokeWidth={2.4} />
                        ) : (
                          <X size={10} strokeWidth={2.6} />
                        )}
                      </span>
                      <span className="flex-1 text-ink">
                        {c.label}
                        <span className="block text-[0.65rem] text-ink-soft mt-0.5">{c.detail}</span>
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-[0.65rem] text-ink-soft/70 leading-relaxed pt-2 border-t border-hairline">
                  Green = done. Amber = present but could be stronger. Grey = missing. Aim for 7+ greens before publishing.
                </p>
              </div>
            </aside>
          </div>
        )}
      </main>

      {/* PREVIEW MODAL — renders the in-progress draft using the same markdown
          pipeline & .prose-pip styles as the live /insights/{slug} page so the
          author (or Wingman) can see exactly how it will appear before saving.
          Pulls live from the local `draft` state — no API round-trip required. */}
      {previewing && (
        <div
          className="fixed inset-0 z-[70] flex items-stretch md:items-start justify-center bg-ink/70 backdrop-blur-sm overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="insight-preview-title"
          data-testid="admin-insight-preview-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreviewing(false);
          }}
        >
          <div className="relative bg-cream w-full md:max-w-4xl md:my-10 md:rounded-3xl border border-hairline shadow-[0_30px_80px_-15px_rgba(0,0,0,0.4)]">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-4 px-6 md:px-10 py-4 bg-cream/95 backdrop-blur border-b border-hairline md:rounded-t-3xl">
              <div className="flex items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1.5 bg-sage/15 text-sage-dark px-2.5 py-1 rounded-full tracking-[0.15em] uppercase font-semibold text-[0.65rem]">
                  <Eye size={11} strokeWidth={2.4} /> Live preview
                </span>
                <span className="text-ink-soft hidden sm:inline">
                  /insights/{draft.slug || "(slug not set)"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewing(false)}
                className="inline-flex items-center gap-1.5 text-ink-soft hover:text-ink text-sm font-medium transition"
                data-testid="admin-insight-preview-close"
              >
                <X size={16} strokeWidth={1.8} /> Close preview
              </button>
            </div>

            <div className="px-0 pb-16">
              {/* Header — mirrors InsightDetail.jsx */}
              <section className="pt-10 md:pt-14 pb-8 md:pb-12 bg-gradient-to-b from-cream to-cream-soft">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                  <div className="flex items-center gap-3 text-xs flex-wrap">
                    <span className="text-[0.7rem] tracking-[0.18em] uppercase font-semibold text-sage">
                      {draft.category}
                    </span>
                    <span className="text-ink-soft/40">·</span>
                    <span className="inline-flex items-center gap-1 text-ink-soft">
                      <Clock size={12} strokeWidth={1.8} />{" "}
                      {Math.max(1, Math.round(draft.body.split(/\s+/).filter(Boolean).length / 225))} min read
                    </span>
                  </div>
                  <h1 className="mt-5 font-serif-display text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05]">
                    {draft.title || <span className="text-ink-soft/50 italic">Untitled article</span>}
                  </h1>
                  {draft.subtitle && (
                    <p className="mt-5 text-lg md:text-xl text-ink-soft italic leading-relaxed">
                      {draft.subtitle}
                    </p>
                  )}
                </div>
              </section>

              {/* Featured image */}
              {draft.featured_image && (
                <section className="pb-10">
                  <div className="max-w-4xl mx-auto px-6 md:px-10">
                    <img
                      src={draft.featured_image}
                      alt=""
                      className="w-full rounded-2xl object-cover aspect-[16/9]"
                      loading="eager"
                    />
                  </div>
                </section>
              )}

              {/* Body */}
              <section className="pb-12">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                  <article className="prose prose-lg prose-pip max-w-none">
                    {draft.body ? (
                      <ReactMarkdown>{draft.body}</ReactMarkdown>
                    ) : (
                      <p className="text-ink-soft italic">No body content yet.</p>
                    )}
                  </article>

                  {/* Tags */}
                  {draft.tags && draft.tags.split(",").map((t) => t.trim()).filter(Boolean).length > 0 && (
                    <div className="mt-12 pt-8 border-t border-hairline flex flex-wrap gap-2">
                      {draft.tags.split(",").map((t) => t.trim()).filter(Boolean).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs tracking-wide text-ink-soft border border-hairline rounded-full px-3 py-1"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
